import React from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { getDate } from "src/utils/Date";
import { UI } from "@wwimmo/ui";
import { useNewsImage, IMAGE_STATE, IMAGE_ORIENTATION } from "src/hooks/news-image/useNewsImage";
import styles from "./NewsPreview.module.css";
import { ColorStyle } from "src/utils/Colors";
import { NetworkConfig } from "src/network/NetworkConfig";
import { GetNewsPreviewInformationById_news_newsfiles } from "src/api/generated/GetNewsPreviewInformationById";

interface NewsPreviewProps {
    title: string;
    lead: string;
    date: any | null;
    text: string[] | string;
    imageUrl: string;
    className?: string;
    newsFiles: GetNewsPreviewInformationById_news_newsfiles[];
}

const NewsPreviewBase = (props: NewsPreviewProps) => {
    const { t } = useTranslation();
    const { title, lead, date, text, imageUrl, newsFiles } = props;

    const { image, imageState, imageOrientation, imageContainerMinHeight } = useNewsImage(imageUrl);

    const containsHtml = (str: string) => /<\/?[a-z][\s\S]*>/i.test(str);

    // Render each string in the `text` array
    const renderTextContent = () => {
        if (Array.isArray(text)) {
            return text.map((item, index) => {
                if (containsHtml(item)) {
                    // Render HTML strings using `dangerouslySetInnerHTML`
                    return (
                        <div
                            key={index}
                            dangerouslySetInnerHTML={{
                                __html: item
                            }}
                        />
                    );
                } else {
                    // Render plain text
                    return item.trim() ? <p key={index}>{item}</p> : <br key={index} />;
                }
            });
        } else if (typeof text === "string" && containsHtml(text)) {
            return (
                <div
                    dangerouslySetInnerHTML={{
                        __html: text
                    }}
                />
            );
        } else {
            return <p>{text}</p>;
        }
    };

    const newsFileList = (
        <div className={styles.NewsFileSelectionContainer}>
            {newsFiles.map((file, index) => (
                <div className={styles.NewsFilesItem} key={file.fileid + index}>
                    <a
                        key={file.fileid}
                        href={NetworkConfig.datafileUrl + file.fileid}
                        className={`${styles.DocumentInfoItemsContainer} link-underline`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <UI.Icon icon={UI.SVGIcon.Documents} color={ColorStyle("white")} size="small" />
                        <div className={styles.FileName}>{file.name}</div>
                    </a>
                </div>
            ))}
        </div>
    );

    return (
        <div className={`${styles.NewsPreviewContainer} news-preview-container ${props.className ?? ""}`}>
            <div className={`${styles.NewsPreviewHeaderBar} news-preview-header-bar`}>
                {t("screens.realestate.news_preview.preview")}
            </div>
            <div className={`${styles.NewsPreviewContent} news-preview-content`}>
                <div className={styles.PreviewTitle}>{title}</div>
                <div className={styles.PreviewDate}>{getDate(date)}</div>
                <div className={styles.PreviewLead}>{lead}</div>
                <div
                    className={`${styles.PreviewText} ${
                        imageOrientation === IMAGE_ORIENTATION.PORTRAIT ? styles.Portrait : styles.Landscape
                    }`}
                    style={{ minHeight: imageContainerMinHeight ?? "default" }}
                >
                    {imageState === IMAGE_STATE.ERROR ? undefined : image}
                    {imageState === IMAGE_STATE.INIT ? (
                        <UI.RotatingSpinner className={styles.ImageLoadSpinner} size={30} noLogo />
                    ) : undefined}
                    {renderTextContent()}
                </div>
            </div>
            {newsFileList}
        </div>
    );
};

export const NewsPreview = Sentry.withProfiler(observer(NewsPreviewBase));
